/**
 * Forest plots drawn straight from the estimates and confidence intervals you give.
 *
 * Nothing is pooled or re-estimated here: each study is a square at its estimate with a
 * line to its lower and upper limits, exactly as typed. An optional last row can be drawn
 * as a summary diamond, using whatever pooled result you computed elsewhere.
 *
 * Ratio measures (odds, risk and hazard ratios) are drawn on a log scale with the line of
 * no effect at 1, so that a ratio of 2 and a ratio of 0.5 sit the same distance either
 * side of it. Differences (mean differences, standardised differences, correlations) are
 * drawn on a linear scale with the line of no effect at 0.
 */

import { LINE_HEIGHT, type Rect } from "@/lib/tool-kit/charts/layout";
import { parseLabeledRows } from "@/lib/tool-kit/charts/parse";
import { niceTicks } from "@/lib/tool-kit/charts/scale";
import {
  estimateTextWidth,
  maxTextWidth,
  type TextStyle,
  truncateText,
  wrapText,
} from "@/lib/tool-kit/charts/text";

export type ForestScale = "log" | "linear";

/* ------------------------------------------------------------------- parse */

export interface ForestRow {
  label: string;
  estimate: number;
  lower: number;
  upper: number;
  /** Weight in percent, or null when none was given. */
  weight: number | null;
  summary: boolean;
  line: number;
}

export type ForestIssueKind = "badRow" | "order" | "positive" | "weight";

export interface ForestIssue {
  kind: ForestIssueKind;
  text: string;
}

export interface ForestParse {
  rows: ForestRow[];
  issues: ForestIssue[];
}

export const FOREST_LIMIT = 40;

/** Read `Study, estimate, lower, upper[, weight]` rows, optionally under a header. */
export function parseForest(
  text: string,
  scale: ForestScale,
  summaryLast: boolean
): ForestParse {
  const table = parseLabeledRows(text);
  const rows: ForestRow[] = [];
  const issues: ForestIssue[] = [];
  for (const entry of table.rows) {
    const estimate = entry.values[0] ?? null;
    const lower = entry.values[1] ?? null;
    const upper = entry.values[2] ?? null;
    const weight = entry.values[3] ?? null;
    const label = entry.label || String(entry.line);
    if (estimate === null || lower === null || upper === null) {
      issues.push({ kind: "badRow", text: label });
    } else if (!(lower <= estimate && estimate <= upper)) {
      issues.push({ kind: "order", text: label });
    } else if (scale === "log" && lower <= 0) {
      issues.push({ kind: "positive", text: label });
    } else if (weight !== null && !(weight >= 0)) {
      issues.push({ kind: "weight", text: label });
    } else {
      rows.push({
        label,
        estimate,
        lower,
        upper,
        weight: weight ?? null,
        summary: false,
        line: entry.line,
      });
    }
  }
  const kept = rows.slice(0, FOREST_LIMIT).map((row, i, all) => ({
    ...row,
    summary: summaryLast && all.length > 1 && i === all.length - 1,
  }));
  return { rows: kept, issues };
}

/* ------------------------------------------------------------------- ticks */

const COARSE = [
  [1],
  [1, 2, 5],
  [1, 2, 3, 5, 7],
  [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 8, 9],
];

/** Ticks for a ratio axis: 1, 2, 5 × 10ᵏ and finer sets when the range is narrow. */
export function ratioTicks(low: number, high: number): number[] {
  const first = Math.floor(Math.log10(low)) - 1;
  const last = Math.ceil(Math.log10(high)) + 1;
  let previous: number[] = [];
  for (const mantissas of COARSE) {
    const ticks: number[] = [];
    for (let power = first; power <= last; power++) {
      for (const m of mantissas) {
        const value = Number((m * 10 ** power).toPrecision(6));
        if (value >= low * 0.9999 && value <= high * 1.0001) {
          ticks.push(value);
        }
      }
    }
    if (ticks.length > 10 && previous.length > 0) {
      return previous;
    }
    if (ticks.length >= 4) {
      return ticks;
    }
    previous = ticks;
  }
  return previous;
}

/* ------------------------------------------------------------------ layout */

export const FOREST_FONT = 12;
const TEXT: TextStyle = { fontSize: FOREST_FONT };
const BOLD: TextStyle = { fontSize: FOREST_FONT, bold: true };
const LINE = FOREST_FONT * LINE_HEIGHT;
const MARGIN = 16;
const COLUMN_GAP = 20;
const NAME_WRAP = 190;
const PLOT_WIDTH = 330;
const HEADER_HEIGHT = 28;
const ROW_PAD = 8;
const MIN_ROW = 24;
const AXIS_GAP = 10;
const ARROW_ROOM = 16;
const SQUARE_MIN = 3.5;
const SQUARE_MAX = 9;
const SQUARE_DEFAULT = 5;
export const DIAMOND_HALF_HEIGHT = 7;

export interface ForestHeaders {
  study: string;
  estimate: string;
  weight: string;
}

export interface ForestOptions {
  scale: ForestScale;
  /** Formats an estimate or limit for the text columns. */
  format: (value: number) => string;
  /** Formats a weight (a percentage) for the weight column. */
  weightFormat: (value: number) => string;
  /** Formats a tick label. */
  tick: (value: number) => string;
  /** Written between the limits: `, ` or `; `. */
  separator: string;
  headers: ForestHeaders;
  /** Labels under the axis: the side the plot favours at each end (may be empty). */
  leftLabel: string;
  rightLabel: string;
  xTitle: string;
}

export interface PlacedForestRow {
  row: ForestRow;
  lines: string[];
  /** Top of the row. */
  top: number;
  height: number;
  /** Vertical centre of the markers and the text. */
  centreY: number;
  x: number;
  xLower: number;
  xUpper: number;
  /** Half the side of the square (summary rows use the diamond instead). */
  half: number;
  text: string;
  weightText: string;
}

export interface ForestLayout {
  width: number;
  height: number;
  scale: ForestScale;
  rows: PlacedForestRow[];
  nameX: number;
  estimateX: number;
  weightX: number | null;
  headerY: number;
  plot: Rect;
  nullX: number;
  axisY: number;
  ticks: { x: number; label: string }[];
  leftLabel: { text: string; x: number; y: number } | null;
  rightLabel: { text: string; x: number; y: number } | null;
  xTitle: { text: string; x: number; y: number } | null;
  /** Bounding boxes of the text columns, for the tests. */
  textRects: Rect[];
}

function domainFor(
  rows: readonly ForestRow[],
  scale: ForestScale
): { low: number; high: number; ticks: number[] } {
  const lows = rows.map((row) => row.lower);
  const highs = rows.map((row) => row.upper);
  if (scale === "log") {
    const low = Math.min(...lows, 1);
    const high = Math.max(...highs, 1);
    const pad = 0.05 * (Math.log(high) - Math.log(low) || 1);
    const lowEdge = Math.exp(Math.log(low) - pad);
    const highEdge = Math.exp(Math.log(high) + pad);
    return {
      low: lowEdge,
      high: highEdge,
      ticks: ratioTicks(lowEdge, highEdge),
    };
  }
  const axis = niceTicks(Math.min(...lows, 0), Math.max(...highs, 0), {
    includeZero: true,
  });
  return { low: axis.min, high: axis.max, ticks: axis.ticks };
}

function squareHalf(row: ForestRow, maxWeight: number): number {
  if (row.weight === null || !(maxWeight > 0)) {
    return SQUARE_DEFAULT;
  }
  return (
    SQUARE_MIN + (SQUARE_MAX - SQUARE_MIN) * Math.sqrt(row.weight / maxWeight)
  );
}

/** Lay the plot out: names at the left, the plot in the middle, numbers at the right. */
export function layoutForest(
  rows: readonly ForestRow[],
  options: ForestOptions
): ForestLayout {
  const { scale, format, headers } = options;
  const nameLines = rows.map((row) =>
    wrapText(row.label || " ", NAME_WRAP, row.summary ? BOLD : TEXT, 3)
  );
  const texts = rows.map(
    (row) =>
      `${format(row.estimate)} [${format(row.lower)}${options.separator}${format(row.upper)}]`
  );
  const withWeights = rows.some((row) => row.weight !== null);
  const weightTexts = rows.map((row) =>
    row.weight === null ? "" : `${options.weightFormat(row.weight)}%`
  );
  const nameWidth = Math.ceil(
    Math.max(
      maxTextWidth(nameLines.flat(), BOLD),
      estimateTextWidth(headers.study, BOLD)
    )
  );
  const textWidth = Math.ceil(
    Math.max(
      maxTextWidth(texts, TEXT),
      estimateTextWidth(headers.estimate, BOLD)
    )
  );
  const weightWidth = Math.ceil(
    Math.max(
      maxTextWidth(weightTexts, TEXT),
      estimateTextWidth(headers.weight, BOLD)
    )
  );

  const nameX = MARGIN;
  const plotX = nameX + nameWidth + COLUMN_GAP;
  const estimateX = plotX + PLOT_WIDTH + COLUMN_GAP;
  const weightX = withWeights ? estimateX + textWidth + COLUMN_GAP : null;
  const right =
    weightX === null ? estimateX + textWidth : weightX + weightWidth;

  const domain = domainFor(rows, scale);
  const toX =
    scale === "log"
      ? (v: number) =>
          plotX +
          ((Math.log(v) - Math.log(domain.low)) /
            (Math.log(domain.high) - Math.log(domain.low))) *
            PLOT_WIDTH
      : (v: number) =>
          plotX + ((v - domain.low) / (domain.high - domain.low)) * PLOT_WIDTH;
  const maxWeight = Math.max(0, ...rows.map((row) => row.weight ?? 0));

  let cursor = MARGIN + HEADER_HEIGHT;
  const placed: PlacedForestRow[] = rows.map((row, i) => {
    const height = Math.max(MIN_ROW, nameLines[i].length * LINE + ROW_PAD);
    const entry: PlacedForestRow = {
      row,
      lines: nameLines[i],
      top: cursor,
      height,
      centreY: cursor + height / 2,
      x: toX(row.estimate),
      xLower: toX(row.lower),
      xUpper: toX(row.upper),
      half: squareHalf(row, maxWeight),
      text: texts[i],
      weightText: weightTexts[i],
    };
    cursor += height;
    return entry;
  });
  const rowsBottom = cursor;
  const axisY = rowsBottom + AXIS_GAP;
  const nullValue = scale === "log" ? 1 : 0;
  const nullX = toX(nullValue);
  const ticks = domain.ticks.map((value) => ({
    x: toX(value),
    label: options.tick(value),
  }));
  const tickBottom = axisY + 5 + 4 + LINE;
  const favourY = tickBottom + 4 + LINE / 2 + FOREST_FONT * 0.35;
  const halfWidth = PLOT_WIDTH / 2 - 8;
  const leftText = truncateText(
    options.leftLabel,
    halfWidth - ARROW_ROOM,
    TEXT
  );
  const rightText = truncateText(
    options.rightLabel,
    halfWidth - ARROW_ROOM,
    TEXT
  );
  const hasFavour = options.leftLabel !== "" || options.rightLabel !== "";
  const favourBottom = hasFavour ? favourY + LINE / 2 + 4 : tickBottom;
  const titleY = favourBottom + 6 + FOREST_FONT;
  const bottom = (options.xTitle === "" ? favourBottom : titleY + 6) + MARGIN;

  const textRects: Rect[] = [];
  for (const entry of placed) {
    const nameHeight = entry.lines.length * LINE;
    textRects.push({
      x: nameX,
      y: entry.centreY - nameHeight / 2,
      width: Math.ceil(
        maxTextWidth(entry.lines, entry.row.summary ? BOLD : TEXT)
      ),
      height: nameHeight,
    });
    textRects.push({
      x: estimateX,
      y: entry.centreY - LINE / 2,
      width: Math.ceil(estimateTextWidth(entry.text, TEXT)),
      height: LINE,
    });
  }
  return {
    width: Math.ceil(right + MARGIN),
    height: Math.ceil(bottom),
    scale,
    rows: placed,
    nameX,
    estimateX,
    weightX,
    headerY: MARGIN + HEADER_HEIGHT / 2,
    plot: {
      x: plotX,
      y: MARGIN + HEADER_HEIGHT,
      width: PLOT_WIDTH,
      height: rowsBottom - MARGIN - HEADER_HEIGHT,
    },
    nullX,
    axisY,
    ticks,
    leftLabel:
      options.leftLabel === ""
        ? null
        : { text: leftText, x: plotX, y: favourY },
    rightLabel:
      options.rightLabel === ""
        ? null
        : { text: rightText, x: plotX + PLOT_WIDTH, y: favourY },
    xTitle:
      options.xTitle === ""
        ? null
        : {
            text: truncateText(options.xTitle, PLOT_WIDTH, TEXT),
            x: plotX + PLOT_WIDTH / 2,
            y: titleY,
          },
    textRects,
  };
}

/* ------------------------------------------------------- text alternative */

export interface ForestTextRow {
  label: string;
  estimate: number;
  lower: number;
  upper: number;
  weight: number | null;
  summary: boolean;
}

export function forestRows(parse: ForestParse): ForestTextRow[] {
  return parse.rows.map((row) => ({
    label: row.label,
    estimate: row.estimate,
    lower: row.lower,
    upper: row.upper,
    weight: row.weight,
    summary: row.summary,
  }));
}
